import { useQuery } from "@tanstack/react-query";
import { GEO_ROUTES, geoResponseSchema, type OperatingCountry } from "@nh/shared";
import { apiFetch } from "@/shared/api/client";

/**
 * ═══════════════════════════════════════════════════════════
 * Desde qué país se conecta quien mira (Anahí, 14-sep-2026)
 *
 * «Que detecte en qué país se está conectando y en automático ponga el
 * precio: si está en Estados Unidos, dólares; si está en México, pesos.
 * El idioma sí se puede cambiar; la moneda no.»
 *
 * La respuesta buena la da el servidor (`/api/geo`, por la IP: Hosting
 * pone la cabecera `x-country-code`). Mientras llega —y si no llega—
 * el navegador adivina con su ZONA HORARIA, que es lo más cercano a
 * "dónde está este aparato" sin pedir permiso de ubicación: un teléfono
 * en Houston está en America/Chicago. Lo último que se supo se guarda
 * en `sessionStorage` para que la segunda pantalla de la misma visita no
 * parpadee de pesos a dólares.
 *
 * Solo MX o US: un tercer país no tiene cuenta de Stripe, y se le trata
 * como la casa (México), que es lo que hacía el sistema hasta hoy.
 * ═══════════════════════════════════════════════════════════
 */

const STORAGE_KEY = "shop_connection_country";

const US_ZONES = new Set([
  "America/New_York", "America/Detroit", "America/Kentucky/Louisville", "America/Kentucky/Monticello",
  "America/Indiana/Indianapolis", "America/Indiana/Vincennes", "America/Indiana/Winamac",
  "America/Indiana/Marengo", "America/Indiana/Petersburg", "America/Indiana/Vevay",
  "America/Chicago", "America/Indiana/Tell_City", "America/Indiana/Knox", "America/Menominee",
  "America/North_Dakota/Center", "America/North_Dakota/New_Salem", "America/North_Dakota/Beulah",
  "America/Denver", "America/Boise", "America/Phoenix", "America/Los_Angeles", "America/Anchorage",
  "America/Juneau", "America/Sitka", "America/Metlakatla", "America/Yakutat", "America/Nome",
  "America/Adak", "Pacific/Honolulu", "US/Eastern", "US/Central", "US/Mountain", "US/Pacific",
  "US/Alaska", "US/Hawaii", "US/Arizona",
]);

const MX_ZONES = new Set([
  "America/Mexico_City", "America/Cancun", "America/Merida", "America/Monterrey", "America/Matamoros",
  "America/Chihuahua", "America/Ciudad_Juarez", "America/Ojinaga", "America/Hermosillo",
  "America/Mazatlan", "America/Bahia_Banderas", "America/Tijuana", "Mexico/General",
  "Mexico/BajaNorte", "Mexico/BajaSur",
]);

/** La conjetura del navegador: zona horaria, y si no, el idioma del sistema. */
export function guessCountryFromDevice(): OperatingCountry | null {
  try {
    const zone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (zone && US_ZONES.has(zone)) return "US";
    if (zone && MX_ZONES.has(zone)) return "MX";
  } catch {
    // Sin Intl no hay zona; se sigue con el idioma.
  }
  const language = typeof navigator !== "undefined" ? navigator.language : "";
  if (/-US$/i.test(language)) return "US";
  if (/-MX$/i.test(language)) return "MX";
  return null;
}

function readStored(): OperatingCountry | null {
  try {
    const stored = window.sessionStorage.getItem(STORAGE_KEY);
    return stored === "MX" || stored === "US" ? stored : null;
  } catch {
    return null;
  }
}

function remember(country: OperatingCountry | null): void {
  try {
    if (country) window.sessionStorage.setItem(STORAGE_KEY, country);
  } catch {
    // No poder recordarlo no impide usarlo en esta visita.
  }
}

/**
 * El país de conexión, o `null` si no es MX ni US (ni el servidor ni el
 * aparato lo saben). Nunca se queda "cargando": mientras el servidor
 * contesta, vale lo último guardado o la conjetura del aparato.
 */
export function useConnectionCountry(): OperatingCountry | null {
  const query = useQuery({
    queryKey: ["geo", "connection-country"],
    queryFn: async () => {
      const data = await apiFetch(GEO_ROUTES.detect, geoResponseSchema, { baseUrl: "" });
      // El servidor manda cuando SABE; si no sabe, la conjetura del aparato.
      const country = data.country ?? guessCountryFromDevice();
      remember(country);
      return country;
    },
    staleTime: Number.POSITIVE_INFINITY,
    gcTime: Number.POSITIVE_INFINITY,
    retry: 1,
  });

  if (query.data !== undefined) return query.data;
  return readStored() ?? guessCountryFromDevice();
}
